/**
 * Item columns for the Rooms matrix grid.
 *
 * Each column maps to an exact item_name in the catalogue (`items` table).
 * The strings here MUST match the catalogue spelling and spacing exactly,
 * because the matrix stores items as a jsonb map keyed on the same names.
 * Inconsistent spacing in the catalogue (e.g. "Topper : Large" with a space
 * before the colon, "Beds: Large" without) is preserved here on purpose.
 *
 * Mode controls grid visibility:
 *   simple: always visible (beds + pillows)
 *   advanced: hidden until "Show extras" is toggled on
 *
 * Non-room items (Accommodation, Minimum Fee Surcharge, Travel) are NOT
 * in this list. They are charges, not room inventory.
 */

export type ItemMode = 'simple' | 'advanced';

export interface ItemColumn {
  /** Catalogue item_name, used verbatim as the items jsonb key. */
  key: string;
  /** Short header label for the grid column. */
  label: string;
  mode: ItemMode;
  /** Display group for the advanced section, helps the operator scan. */
  group?: string;
}

export const ITEM_COLUMNS: ItemColumn[] = [
  // Simple (always visible)
  { key: 'Beds: Large', label: 'Beds L', mode: 'simple', group: 'Beds' },
  { key: 'Beds: Small', label: 'Beds S', mode: 'simple', group: 'Beds' },
  { key: 'Pillows : Large', label: 'Pillows L', mode: 'simple', group: 'Pillows' },
  { key: 'Pillows : Small', label: 'Pillows S', mode: 'simple', group: 'Pillows' },

  // Advanced (under Show extras)
  { key: 'Topper : Large', label: 'Topper L', mode: 'advanced', group: 'Toppers' },
  { key: 'Topper : Small', label: 'Topper S', mode: 'advanced', group: 'Toppers' },
  { key: 'Electric Blankets : Large', label: 'Blanket L', mode: 'advanced', group: 'Blankets' },
  { key: 'Electric Blankets : Small', label: 'Blanket S', mode: 'advanced', group: 'Blankets' },
  { key: 'Headboard - Large', label: 'Headboard L', mode: 'advanced', group: 'Headboards' },
  { key: 'Headboard: Medium', label: 'Headboard M', mode: 'advanced', group: 'Headboards' },
  { key: 'Headboard: Small', label: 'Headboard S', mode: 'advanced', group: 'Headboards' },
  { key: 'Ottoman: Large', label: 'Ottoman L', mode: 'advanced', group: 'Ottomans' },
  { key: 'Ottoman: Small', label: 'Ottoman S', mode: 'advanced', group: 'Ottomans' },
  { key: 'Sofa: 1 Seater', label: 'Sofa 1', mode: 'advanced', group: 'Sofas' },
  { key: 'Sofa: 2 Seater', label: 'Sofa 2', mode: 'advanced', group: 'Sofas' },
  { key: 'Sofa: 3 Seater', label: 'Sofa 3', mode: 'advanced', group: 'Sofas' },
  { key: 'Sofa: 4 Seater', label: 'Sofa 4', mode: 'advanced', group: 'Sofas' },
  { key: 'Sofa: 5 Seater', label: 'Sofa 5', mode: 'advanced', group: 'Sofas' },
  { key: 'Sofa: 6 Seater', label: 'Sofa 6', mode: 'advanced', group: 'Sofas' },
  { key: 'Cots', label: 'Cots', mode: 'advanced', group: 'Other' },
  { key: 'Chair: Fabric / Upholstered', label: 'Chairs', mode: 'advanced', group: 'Other' },
];

/** Items not in this set are still preserved on the row but invisible. */
export const ITEM_KEYS = new Set(ITEM_COLUMNS.map((c) => c.key));
